// Putting a share on the clipboard: text through the Clipboard API with the
// old textarea copy as a fallback, a text still on its way (the share link)
// as a promised ClipboardItem; an image through ClipboardItem where the
// browser allows it, otherwise downloaded as a file.

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    area.remove();
    return ok;
  }
}

/**
 * Copy a text that is still on its way, such as a link the Worker is creating.
 * Safari only lets a page write to the clipboard during the tap itself, so the
 * text goes in as a promise inside a ClipboardItem, which Safari and Chrome
 * wait for; elsewhere it is copied once it arrives. Rejects when the text does.
 */
export async function copyTextWhenReady(text: Promise<string>): Promise<boolean> {
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'text/plain': text.then((t) => new Blob([t], { type: 'text/plain' })) })]);
      return true;
    } catch {
      // No promised items here, or a denied permission: copy the plain way below.
    }
  }
  return copyText(await text);
}

export type ImageShareResult = 'copied' | 'downloaded' | 'failed';

export async function copyOrDownloadImage(blob: Blob, filename: string): Promise<ImageShareResult> {
  try {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
      return 'copied';
    }
  } catch {
    // Firefox before 127, an insecure origin, or a denied permission: download instead.
  }
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return 'downloaded';
  } catch {
    return 'failed';
  }
}
